import { all, call, takeLatest, put, select } from 'redux-saga/effects';

import apis from '../apis/chat';
import { LOADING } from '@coconut/constants/loadingState';
import { IMAGE, TAGGING } from '@coconut/constants/mimeTypes';
import {
  types,
  fetchChatsSuccess,
  fetchChatsFailed,
  refetchHistoricalMessagesSuccess,
  refetchHistoricalMessagesError,
  getCurrentMessages,
  newMessageReadFailed,
  saveUnSentMessageSuccess,
  getUnSentMessage,
} from '../redux/chatsMessage';
import { subscribeChannel } from '../redux/pubnub';
import { storeImages } from '../redux/images';
import { CONTENT_ACTION } from '../constants/messageActions';
import { getUserId, getRole } from '@coconut/services/redux/profile';
import { TUTOR } from '@coconut/constants/userRole';
import { setCookie } from '@coconut/services/cookies';
import { UN_SENT_MESSAGE } from '@coconut/constants/cookieName';
import { getSelectedQuestionID } from '@coconut/scenes/Chat/services/redux/selectQuestion';

function* formatHistoricalMessages(messages) {
  const formattedMessages = [];
  for (let i = 0; i < messages.length; i++) {
    const currentMessage = messages[i];
    // First historical message will have a avatar
    // by default (no comparison required)
    if (i === 0) {
      formattedMessages.push({
        ...currentMessage,
      });
      continue;
    }
    const previousMessage = messages[i - 1];
    // If the currentMessage has same UID as previous,
    // return as is.
    if (previousMessage.snapask_uid === currentMessage.snapask_uid) {
      // If the previous message action is same
      // as current.
      if (previousMessage.message_action === currentMessage.message_action) {
        formattedMessages.push({
          ...currentMessage,
        });
        continue;
      }

      if (
        previousMessage.message_action === CONTENT_ACTION &&
        previousMessage.message_action !== currentMessage.message_action
      ) {
        formattedMessages.push({
          ...currentMessage,
        });
        continue;
      }

      formattedMessages.push({
        ...currentMessage,
      });
      continue;
    }

    // If UID of messages aren't same
    // add avatar to message object.
    formattedMessages.push({
      ...currentMessage,
    });
  }

  return formattedMessages;
}

function* fetchChatsFlow() {
  const QID = yield select(getSelectedQuestionID);
  const userID = yield select(getUserId);

  try {
    const response = yield call(apis.fetchChats, QID);

    const {
      data: {
        messages: historicalMessages,
        pubnub_channel_name: channelName,
        last_tutor_read_message_id: lastTutorReadMessageId, // determine which message has been read by tutor.
      },
    } = response;

    const messages = yield call(
      formatHistoricalMessages,
      historicalMessages.reverse()
    );

    yield put(
      fetchChatsSuccess({
        messages,
        channelName,
        lastTutorReadMessageId: lastTutorReadMessageId || 0,
      })
    );

    // filter out image messages and store those message information
    // to image reducer. The only possible way to get images is from
    // parsing the messages, thus image object is going to have the same
    // structure as normal message.
    const imageMessages = messages.filter((message) => message.mtype === IMAGE);

    yield put(storeImages(imageMessages));

    // subscribe to dedicated channel
    yield put(subscribeChannel(channelName, userID));
  } catch (err) {
    yield put(fetchChatsFailed(err));
  }
}

function* retchHistoricalMessagesFlow(action) {
  const { QID } = action.payload;

  try {
    const response = yield call(apis.fetchChats, QID);

    const {
      data: { messages: historicalMessages },
    } = response;

    const messages = yield call(
      formatHistoricalMessages,
      historicalMessages.reverse()
    );

    yield put(refetchHistoricalMessagesSuccess(messages));

    // filter out image messages and store those message information
    // to image reducer. The only possible way to get images is from
    // parsing the messages, thus image object is going to have the same
    // structure as normal message.
    const imageMessages = messages.filter((message) => message.mtype === IMAGE);

    yield put(storeImages(imageMessages));
  } catch (err) {
    yield put(refetchHistoricalMessagesError(err));
  }
}

function* messageReadFlow(action) {
  const userRole = yield select(getRole);

  try {
    if (userRole === TUTOR) {
      const userId = yield select(getUserId);
      const currentMessages = yield select(getCurrentMessages);
      const { questionID } = action.payload;

      // get last messageID
      const [latestMessage = {}] = currentMessages
        .filter((m) => m.mtype !== TAGGING)
        .reverse();
      // do nothing when id is 0, and prevent to trigger reading when message is not ready
      if (!latestMessage.id || latestMessage.loadingStatus === LOADING) {
        return;
      }

      yield call(apis.readMessage, {
        QID: questionID,
        userID: userId,
        messageID: latestMessage.id,
      });
    }
  } catch (error) {
    yield put(newMessageReadFailed(error));
  }
}

function* saveUnSentMessageFlow(action) {
  const { QID, message } = action.payload;

  const currentUnSentMessageInCookie = yield select(getUnSentMessage);
  const newUnSentMessage = Object.assign(currentUnSentMessageInCookie, {
    [QID]: message,
  });

  setCookie(UN_SENT_MESSAGE, JSON.stringify(newUnSentMessage));

  yield put(saveUnSentMessageSuccess(QID, message));
}

export default function* () {
  yield all([
    takeLatest(types.FETCH_CHATS, fetchChatsFlow),
    takeLatest(types.REFETCH_HISTORICAL_MESSAGES, retchHistoricalMessagesFlow),
    takeLatest(types.NEW_MESSAGE_READ, messageReadFlow),
    takeLatest(types.SAVE_UN_SENT_MESSAGE, saveUnSentMessageFlow),
  ]);
}



// WEBPACK FOOTER //
// ./src/scenes/Chat/services/sagas/chatMessages.js